// Authentication context provider
// Manages user authentication state, server-side session validation, and auto-disconnect
// CHANGE: Converted lastActivityTime from useState to useRef to avoid re-creating
//         5 event listeners + 1 interval on every user interaction
// CHANGE: Removed all debug console.log statements

import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { UserWithSites } from '../lib/supabase';
import { getCurrentUser, loadUserProfile, signOut, updateLastActivity, isNetworkError } from '../lib/auth';
import { setSentryUser, addBreadcrumb } from '../lib/sentry';

interface AuthContextType {
  user: UserWithSites | null;
  loading: boolean;
  refreshUser: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const INACTIVITY_TIMEOUT = 30 * 60 * 1000;
const LAST_ACTIVITY_KEY = 'tasq_last_activity';
const PROFILE_REFRESH_INTERVAL = 5 * 60 * 1000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserWithSites | null>(null);
  const [loading, setLoading] = useState(true);

  const getInitialActivityTime = (): number => {
    const stored = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (stored) {
      const timestamp = parseInt(stored, 10);
      if (!isNaN(timestamp) && timestamp <= Date.now()) {
        return timestamp;
      }
    }
    return Date.now();
  };

  const lastActivityTimeRef = useRef<number>(getInitialActivityTime());

  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    if (user) {
      setSentryUser({ id: user.id, username: user.username, role: user.role });
    } else {
      setSentryUser(null);
    }
  }, [user]);

  const refreshUser = useCallback(async () => {
    const currentUser = await getCurrentUser();
    setUser(currentUser);
  }, []);

  const logout = useCallback(async () => {
    addBreadcrumb('auth', 'Deconnexion utilisateur');
    await signOut();
    setUser(null);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  }, []);

  const handleActivity = useCallback(() => {
    const now = Date.now();
    lastActivityTimeRef.current = now;
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    if (userRef.current) {
      updateLastActivity(userRef.current.id);
    }
  }, []);

  const isActivityTimestampExpired = useCallback((): boolean => {
    const stored = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!stored) return false;

    const lastActivity = parseInt(stored, 10);
    if (isNaN(lastActivity)) return true;

    const now = Date.now();
    if (lastActivity > now + 60000) {
      return true;
    }

    return (now - lastActivity) >= INACTIVITY_TIMEOUT;
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      if (isActivityTimestampExpired()) {
        await signOut();
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        setLoading(false);
        return;
      }

      try {
        const currentUser = await getCurrentUser();
        setUser(currentUser);

        if (currentUser) {
          const now = Date.now();
          lastActivityTimeRef.current = now;
          localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
        }
      } catch {
      }

      setLoading(false);
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        (async () => {
          const currentUser = await getCurrentUser();
          setUser(currentUser);
          const now = Date.now();
          lastActivityTimeRef.current = now;
          localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
          addBreadcrumb('auth', 'Connexion reussie', { userId: session.user.id });
        })();
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        (async () => {
          const currentUser = await getCurrentUser();
          if (!currentUser) {
            await signOut();
            setUser(null);
            localStorage.removeItem(LAST_ACTIVITY_KEY);
            return;
          }
          setUser(currentUser);
        })();
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        localStorage.removeItem(LAST_ACTIVITY_KEY);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [isActivityTimestampExpired]);

  const userId = user?.id;

  useEffect(() => {
    if (!userId) return;

    const intervalId = setInterval(async () => {
      try {
        const { data: { user: authUser }, error } = await supabase.auth.getUser();

        if (error) {
          if (isNetworkError(error)) return;
          await logout();
          return;
        }

        if (!authUser) {
          await logout();
          return;
        }

        const currentUser = await loadUserProfile(authUser.id);
        if (!currentUser) {
          await logout();
          return;
        }

        setUser(currentUser);
        addBreadcrumb('auth', 'Profil rafraichi automatiquement');
      } catch (error) {
        if (!isNetworkError(error)) {
          // Silent fail for non-network errors
        }
      }
    }, PROFILE_REFRESH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [userId, logout]);

  useEffect(() => {
    if (!userId) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      if (isActivityTimestampExpired()) {
        await logout();
        return;
      }

      try {
        const { data: { user: authUser }, error } = await supabase.auth.getUser();

        if (error && !isNetworkError(error)) {
          await logout();
          return;
        }

        if (!error && !authUser) {
          await logout();
          return;
        }
      } catch (error) {
        if (isNetworkError(error)) {
          // Skip validation on network error
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [userId, logout, isActivityTimestampExpired]);

  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    const checkInactivity = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivityTimeRef.current;
      if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
        addBreadcrumb('auth', 'Deconnexion pour inactivite', { inactivityMs: timeSinceLastActivity });
        logout();
      }
    }, 60000);

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearInterval(checkInactivity);
    };
  }, [user, logout, handleActivity]);

  return (
    <AuthContext.Provider value={{ user, loading, refreshUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
